import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { SchemaProvider } from './contexts/SchemaContext';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import Hero from './components/Hero';
import MarketMarquee from './components/MarketMarquee';
import Reviews from './components/Reviews';
import Footer from './components/Footer';
import HomepageContent from './components/HomepageContent';
import SidebarNotification from './components/SidebarNotification';
import WhatsAppButton from './components/WhatsAppButton';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';

const About = lazy(() => import('./components/About'));
const Contact = lazy(() => import('./components/Contact'));
const Blog = lazy(() => import('./components/Blog'));
const YouTubeShorts = lazy(() => import('./components/YouTubeShorts'));
const FAQSection = lazy(() => import('./components/FAQ'));
const Services = lazy(() => import('./components/Services')); // This is the component used in HomePage

const Equity = lazy(() => import('./pages/Equity'));
const IndexOptions = lazy(() => import('./pages/IndexOptions'));
const ResearchDashboard = lazy(() => import('./pages/ResearchPage'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const Academy = lazy(() => import('./pages/Academy'));
const BlogPage = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ClientConsent = lazy(() => import('./pages/ClientConsent'));
const ClientForm = lazy(() => import('./pages/ClientForm'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Disclosure = lazy(() => import('./pages/Disclosure'));
const ServiceDisclosure = lazy(() => import('./pages/ServiceDisclosure'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const ComplaintsRedressal = lazy(() => import('./pages/ComplaintsRedressal'));
const Complaints = lazy(() => import('./pages/Complaints'));
const GrievanceRedressal = lazy(() => import('./pages/GrievanceRedressal'));
const ServicesPage = lazy(() => import('./pages/Services')); // Renamed to avoid conflict
const FAQPage = lazy(() => import('./pages/FAQ'));
const CareerPage = lazy(() => import('./pages/CareerPage'));
const EquityResearch = lazy(() => import('./pages/EquityResearch'));
const FinancialAdviser = lazy(() => import('./pages/FinancialAdviser'));
const PortfolioManagement = lazy(() => import('./pages/PortfolioManagement'));
const RiskManagement = lazy(() => import('./pages/RiskManagement'));
const JigneshProfile = lazy(() => import('./pages/JigneshProfile'));
const BlogSlug = lazy(() => import('./pages/BlogSlug'));
const BlogTrial = lazy(() => import('./pages/BlogTrial'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const ResearchReport = lazy(() => import('./pages/ResearchReport'));
const ResearchReportUpload = lazy(() => import('./pages/ResearchReportUpload'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CreateLink = lazy(() => import('./pages/CreateLink'));
const KycVerificationPage = lazy(() => import('./pages/KycVerificationPage'));
const GovtEsignGateway = lazy(() => import('./pages/GovtEsignGateway'));

// Scroll to section handler
const ScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return null;
};

const LazySection = ({ children, rootMargin = '300px' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return <div ref={sectionRef}>{isVisible ? children : null}</div>;
};

// Home page component
const HomePage = () => (
  <>
    <ScrollHandler />
    <Hero />
    {/* <MarketMarquee /> */}
    <Suspense fallback={null}>
      <Services /> {/* Uses ./components/Services */}
    </Suspense>
    <Reviews />
    <HomepageContent />
    <LazySection>
      <Suspense fallback={null}>
        <Blog />
      </Suspense>
    </LazySection>
    <LazySection>
      <Suspense fallback={null}>
        <YouTubeShorts />
      </Suspense>
    </LazySection>
    <LazySection>
      <Suspense fallback={null}>
        <About />
      </Suspense>
    </LazySection>
    <LazySection>
      <Suspense fallback={null}>
        <FAQSection />
      </Suspense>
    </LazySection>
    <LazySection>
      <Suspense fallback={null}>
        <Contact />
      </Suspense>
    </LazySection>
    {/* Telegram prompt only on Home (shows after 10s) */}
    <SidebarNotification />
    {/* Fixed WhatsApp icon on bottom-left thii*/}
    <WhatsAppButton />
  </>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}



function App() {
  return (
    <SchemaProvider>
      <Router>
      <ScrollToTop />
      <div className="App">
        <Header />
        <Suspense fallback={null}>
            <ErrorBoundary>
<Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/aboutus" element={<AboutUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/research-dashboard" element={<ResearchDashboard />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/contactus" element={<ContactPage />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/blog" element={<BlogPage />} />
            {/* New slug-based blog detail route */}
            <Route path="/blog/:slug" element={<BlogSlug />} />
            {/* Legacy id-based route retained temporarily */}
            <Route path="/blog/:id" element={<BlogPost />} />

            {/* Duplicate routes using plural path to match updated navigation */}
            <Route path="/blogs" element={<BlogPage />} />
            <Route path="/blogs/:slug" element={<BlogPost />} />
            <Route path="/client-consent" element={<ClientConsent />} />
            <Route path="/client-form" element={<ClientForm />} />

            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/disclosure" element={<Disclosure />} />
            <Route path="/service-disclosure" element={<ServiceDisclosure />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund_policy" element={<RefundPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cancellation-and-refund-policy" element={<RefundPolicy />} />
            <Route path="/complaints-redressal" element={<ComplaintsRedressal />} />
            <Route path="/complaints" element={<Complaints />} />
            <Route path="/grievance-redressal" element={<GrievanceRedressal />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/careers" element={<CareerPage />} />
            <Route path="/services/:serviceType" element={<ServicesPage />} /> {/* Uses ./pages/Services */}
            <Route path="/services" element={<ServicesPage />} /> {/* Uses ./pages/Services */}
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/kyc-verification" element={<KycVerificationPage />} />
            <Route path="/esign/gateway" element={<GovtEsignGateway />} />
            <Route path="/kyc/esign/gateway" element={<GovtEsignGateway />} />
            <Route path="/verify-kyc" element={<Navigate to="/kyc-verification" replace />} />
            <Route path="/ekyc" element={<Navigate to="/kyc-verification" replace />} />
            <Route path="/create-link" element={<CreateLink />} />
            <Route path="/creare-linke" element={<Navigate to="/create-link" replace />} />
            <Route path="/create-linke" element={<Navigate to="/create-link" replace />} />
            <Route path="/generate-link" element={<Navigate to="/create-link" replace />} />
            <Route path="/equity-research" element={<EquityResearch />} />
            <Route path="/financial-adviser" element={<FinancialAdviser />} />
            <Route path="/financial-advisory" element={<Navigate to="/financial-adviser" replace />} />
            <Route path="/portfolio-management" element={<PortfolioManagement />} />
            <Route path="/risk-management" element={<RiskManagement />} />
            <Route path="/profile/jignesh" element={<JigneshProfile />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/blog-trial" element={<BlogTrial />} />
            <Route path="/client-testimonials" element={<Testimonials />} />
            <Route path="/research-report" element={<ResearchReport />} />
            <Route path="/researchreport-uplode" element={<ResearchReportUpload />} />
          </Routes>
        </ErrorBoundary>
        </Suspense>
        <Footer />
      </div>
    </Router>
    </SchemaProvider>
  );
}

export default App;